import { memo, useMemo, type FC } from "react";
import {
  DEFAULT_GRID_STEP_PX,
  type PlaneCoordinate,
  type PlaneScreenPoint,
  type ToScreenPoint,
} from "./CartesianPlane";
import styles from "./DeckBottomViewCard.module.css";
import type { JoistSegment, SupportPoint } from "./deckBottomViewGeometry";

type BottomStructureLayerProps = {
  isStripeSupport: boolean;
  joists: JoistSegment[];
  joistWidthMeters: number;
  supports: SupportPoint[];
  highlightedSupports?: PlaneCoordinate[];
  toScreenPoint: ToScreenPoint;
};

const SUPPORT_RADIUS_M = 2 / DEFAULT_GRID_STEP_PX;
const SUPPORT_STROKE_WIDTH_M = 0.9 / DEFAULT_GRID_STEP_PX;
const HIGHLIGHT_MATCH_TOLERANCE_M = 0.001;
// A stripe-type support is drawn as a thin line over its joist rather than as points - a ratio of
// the joist's own width (not a fixed size) so it stays visibly thinner at any zoom level while the
// joist itself still peeks out on both edges.
const SUPPORT_STRIPE_WIDTH_RATIO = 0.45;
const NO_HIGHLIGHTED_SUPPORTS: PlaneCoordinate[] = [];

const getPixelsPerMeter = (toScreenPoint: ToScreenPoint): number | null => {
  const originPoint = toScreenPoint({ x: 0, y: 0 });
  const unitPoint = toScreenPoint({ x: 1, y: 0 });

  return originPoint && unitPoint ? Math.abs(unitPoint.pixelX - originPoint.pixelX) : null;
};

const isHighlightedSupport = (support: SupportPoint, highlightedSupports: PlaneCoordinate[]): boolean =>
  highlightedSupports.some(
    (highlighted) =>
      Math.abs(highlighted.x - support.x) < HIGHLIGHT_MATCH_TOLERANCE_M &&
      Math.abs(highlighted.y - support.y) < HIGHLIGHT_MATCH_TOLERANCE_M,
  );

// A full circle as a closed sub-path (two half arcs, clockwise like a <circle>), so any number of
// supports share a single path.
const getCircleSubPath = (center: PlaneScreenPoint, radius: number): string =>
  `M${center.pixelX - radius},${center.pixelY}` +
  `a${radius},${radius} 0 1,1 ${radius * 2},0a${radius},${radius} 0 1,1 ${-radius * 2},0Z`;

// The joists (and stripe supports over them) and the point supports: one path per style instead of
// one element per joist/support, painted joists first, then stripes, then supports (highlighted last).
const BottomStructureLayer: FC<BottomStructureLayerProps> = ({
  isStripeSupport,
  joists,
  joistWidthMeters,
  supports,
  highlightedSupports = NO_HIGHLIGHTED_SUPPORTS,
  toScreenPoint,
}) => {
  const pixelsPerMeter = getPixelsPerMeter(toScreenPoint) ?? DEFAULT_GRID_STEP_PX;
  const supportRadius = pixelsPerMeter * SUPPORT_RADIUS_M;

  const { joistsPath, supportsPath, highlightedSupportsPath } = useMemo(() => {
    const supportSubPaths: string[] = [];
    const highlightedSupportSubPaths: string[] = [];

    supports.forEach((support, index) => {
      const point = toScreenPoint(support, index);

      if (point && supportRadius > 0) {
        (isHighlightedSupport(support, highlightedSupports) ? highlightedSupportSubPaths : supportSubPaths).push(
          getCircleSubPath(point, supportRadius),
        );
      }
    });

    return {
      joistsPath: joists
        .map((joist, index) => {
          const startPoint = toScreenPoint(joist.start, index);
          const endPoint = toScreenPoint(joist.end, index);

          return startPoint && endPoint
            ? `M${startPoint.pixelX},${startPoint.pixelY}L${endPoint.pixelX},${endPoint.pixelY}`
            : "";
        })
        .join(""),
      supportsPath: supportSubPaths.join(""),
      highlightedSupportsPath: highlightedSupportSubPaths.join(""),
    };
  }, [highlightedSupports, joists, supportRadius, supports, toScreenPoint]);

  if (joists.length === 0 && supports.length === 0) {
    return null;
  }

  const joistStrokeWidth = pixelsPerMeter * joistWidthMeters;
  const supportStrokeWidth = pixelsPerMeter * SUPPORT_STROKE_WIDTH_M;

  return (
    <svg className={styles.structureLayer} aria-hidden="true">
      {joistsPath && <path className={styles.joist} d={joistsPath} strokeWidth={joistStrokeWidth} />}
      {isStripeSupport && joistsPath && (
        <path
          className={styles.supportStripe}
          d={joistsPath}
          strokeWidth={joistStrokeWidth * SUPPORT_STRIPE_WIDTH_RATIO}
        />
      )}
      {supportsPath && <path className={styles.supportPoint} d={supportsPath} strokeWidth={supportStrokeWidth} />}
      {highlightedSupportsPath && (
        <path className={styles.supportPointHighlighted} d={highlightedSupportsPath} strokeWidth={supportStrokeWidth} />
      )}
    </svg>
  );
};

// Memoized: re-renders only when the structure or the view change.
export default memo(BottomStructureLayer);
